import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import {
  buildKpiDateRange,
  coerceKpiNumber,
  extractDailyRows,
} from "../_kpiDenseDailyUtils";
import {
  buildSportsbookKpiQueryParams,
  parseSportsbookKpiFilterMetadata,
  type SportsbookKpiFilterAvailability,
  type SportsbookKpiFilterOption,
  type SportsbookKpiFilterValues,
} from "./sportsbookKpiFilters";

export type SportsbookDailyPerformanceFilters = SportsbookKpiFilterValues & {
  start_date: string;
  end_date: string;
};

export type SportsbookDailyPoint = {
  date: string;
  turnover: number;
  pnl: number;
};

export type SportsbookDailyFilterOption = SportsbookKpiFilterOption;

/** Filter catalog returned by the daily endpoint under `meta.filters`. */
export type SportsbookDailyFilterOptions = {
  sports: SportsbookDailyFilterOption[];
  leagues: SportsbookDailyFilterOption[];
  betTypes: SportsbookDailyFilterOption[];
  currencyTypes: SportsbookDailyFilterOption[];
};

export type SportsbookDailyResult = {
  points: SportsbookDailyPoint[];
  filterOptions: SportsbookDailyFilterOptions;
  selectedFilters: Required<SportsbookKpiFilterValues>;
  availableFilters: SportsbookKpiFilterAvailability;
};

type RawDailyRow = {
  date?: string | null;
  turnover?: number | string | null;
  pnl?: number | string | null;
  profit_loss?: number | string | null;
  profit_and_loss?: number | string | null;
};

const mapRow = (row: RawDailyRow): SportsbookDailyPoint | null => {
  const date = typeof row?.date === "string" ? row.date.trim() : "";
  if (!date) return null;

  return {
    date,
    turnover: coerceKpiNumber(row.turnover),
    pnl: coerceKpiNumber(row.pnl ?? row.profit_loss ?? row.profit_and_loss),
  };
};

/** Maps the sportsbook daily HTTP envelope into dense chart-ready points. */
export const parseSportsbookDailyResponse = (
  body: unknown,
  filters: SportsbookDailyPerformanceFilters,
): SportsbookDailyPoint[] => {
  const envelope = body as { data?: unknown } | null | undefined;
  const payload = envelope?.data ?? body;
  const rawList = extractDailyRows<RawDailyRow>(payload);

  const byDate = new Map<string, SportsbookDailyPoint>();
  for (const row of rawList) {
    const point = mapRow(row);
    if (point) byDate.set(point.date, point);
  }

  const denseDates = buildKpiDateRange(filters.start_date, filters.end_date);
  if (denseDates.length > 0) {
    return denseDates.map(
      (date) =>
        byDate.get(date) ?? {
          date,
          turnover: 0,
          pnl: 0,
        },
    );
  }

  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
};

export const parseSportsbookDailyFilterOptions = (
  body: unknown,
): SportsbookDailyFilterOptions =>
  parseSportsbookKpiFilterMetadata(body).options;

export const fetchSportsbookDailyPerformance = async (
  filters: SportsbookDailyPerformanceFilters,
): Promise<SportsbookDailyResult> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.SPORTSBOOK_DAILY_PERFORMANCE, {
    params: buildSportsbookKpiQueryParams(filters),
  });
  const metadata = parseSportsbookKpiFilterMetadata(response?.data);
  return {
    points: parseSportsbookDailyResponse(response?.data, filters),
    filterOptions: metadata.options,
    selectedFilters: metadata.selectedValues,
    availableFilters: metadata.available,
  };
};

export const useGetSportsbookDailyPerformance = (
  filters: SportsbookDailyPerformanceFilters,
) => {
  const { start_date, end_date, sport, league, bet_type, money_type } =
    filters;

  return useQuery<SportsbookDailyResult, Error>({
    queryKey: [
      "SPORTSBOOK_DAILY_PERFORMANCE",
      start_date,
      end_date,
      sport,
      league,
      bet_type?.join(","),
      money_type?.join(","),
    ],
    queryFn: () => fetchSportsbookDailyPerformance(filters),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
